import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { OrderedSet, Map } from 'immutable';

import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import InputLabel from '@material-ui/core/InputLabel';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import LinearProgress from '@material-ui/core/LinearProgress';
import InputAdornment from '@material-ui/core/InputAdornment';

import SearchIcon from '@material-ui/icons/Search';

import { makeStyles } from '@material-ui/core/styles';
import theme from '../../../../common/theme';

import { TextHeading, Button as ThemedButton, Spinner } from '../../../../common/components';
import { PathwayCandidateTile, Header, Content, Actions, Banner } from './pathway_candidates_styles';
import UserModel from '../../../../session/models/user_model';
import { useFetchOnce } from '../../../../util/hooks';

const candidateStates = [
  { label: 'Applied', value: 's_applied' },
  { label: 'In Progress', value: 's_in_progress' },
  { label: 'Completed', value: 's_complete' },
  { label: 'Dropped Voluntarily', value: 's_dropped_voluntarily' },
  { label: 'Dropped Involuntarily', value: 's_dropped_involuntarily' },
  { label: 'Saved', value: 's_saved' },
];

const useStyles = makeStyles(() => ({
  statusContainer: {
    flexGrow: 1,
  },
  stepsContainer: {
    flexGrow: 3,
  },
  progressBar: {
    height: '12px',
    borderRadius: '10px',
    width: '80%',
    backgroundColor: theme.color.grayLighter,
    '& > div': {
      backgroundColor: theme.color.greenLight,
      borderRadius: '10px',
    },
  },
  statusSelect: {
    width: '200px',
    marginRight: '20px !important',
  },
  stepsCompletedText: {
    fontWeight: 'bold',
    color: theme.color.greenLight,
    marginBottom: '10px',
  },
}));

const PathwayCandidatesView = ({
  listCandidatePathwaysForPathwayInstance,
  pathwayCandidates,
  user,
  transitionCandidatePathwayStaffState,
  pathwayInstance,
  pathwayDroppedReasonTypes,
  onUpdate,
}) => {
  const classes = useStyles();

  const resolved = useFetchOnce(user, pathwayInstance.get('id'), listCandidatePathwaysForPathwayInstance);

  if (!resolved) {
    return <Spinner />;
  }

  return (
    <div>
      <Banner>{`TOTAL: ${pathwayCandidates.size} Candidates`}</Banner>
      <TextField
        id="search"
        placeholder="Search"
        margin="dense"
        variant="outlined"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <div>
        {pathwayCandidates.map((candidate) => {
          const progressPercentage = (100 * parseInt(candidate.get('completedStepsCount'), 10)) / parseInt(candidate.get('totalStepsCount'), 10);

          return (
            <PathwayCandidateTile key={candidate.get('id')}>
              <Header>
                <div>
                  <Link to={`/staff/pathways/${pathwayInstance.get('id')}/candidates/${candidate.get('id')}`}>
                    <TextHeading size="medium" weight="bold">
                      {`${candidate.getIn(['candidate', 'user', 'firstName'])} ${candidate.getIn(['candidate', 'user', 'lastName'])}`}
                    </TextHeading>
                  </Link>
                </div>
                <div>
                  <Typography>Actual Completion</Typography>
                  <b>{candidate.get('completedOn') ? moment(candidate.get('completedOn')).format('MMM DD YYYY') : ''}</b>
                </div>

                <Actions>
                  <Link to={`/staff/messages/new/${candidate.getIn(['candidate', 'candidateId'])}`}>
                    <ThemedButton buttonType="secondary-outline" className="button">
                      Send Message
                    </ThemedButton>
                  </Link>
                  <Link to={`/staff/pathways/${pathwayInstance.get('id')}/candidates/${candidate.get('id')}`}>
                    <ThemedButton buttonType="secondary-outline" className="button">
                      View
                    </ThemedButton>
                  </Link>
                </Actions>
              </Header>
              <Content>
                <div className={classes.statusContainer}>
                  <FormControl className={classes.statusSelect}>
                    <InputLabel>CANDIDATE STATUS</InputLabel>
                    <Select
                      margin="dense"
                      variant="outlined"
                      value={candidate.get('staffState')}
                      onChange={(e) => transitionCandidatePathwayStaffState(user, candidate.get('id'), e.target.value.substring(2))}
                    >
                      {candidateStates.map((candidateState) => (
                        <MenuItem key={candidateState.value} value={candidateState.value}>
                          {candidateState.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {(candidate.get('staffState') === 's_dropped_voluntarily' || candidate.get('staffState') === 's_dropped_involuntarily') && (
                    <FormControl className={`${classes.statusSelect}`}>
                      <InputLabel>DROPPED REASON</InputLabel>
                      <Select margin="dense" variant="outlined" onChange={(e) => onUpdate(candidate.get('id'), { pathwayDroppedReasonType: e.target.value })}>
                        {pathwayDroppedReasonTypes.map((droppedReason) => (
                          <MenuItem key={droppedReason[0]} value={droppedReason[0]}>
                            {droppedReason[1]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </div>
                <div className={classes.stepsContainer}>
                  <Typography variant="h6" className={classes.stepsCompletedText}>
                    Steps Completed:
                    {` ${candidate.get('completedStepsCount')}/${candidate.get('totalStepsCount')}`}
                  </Typography>
                  <LinearProgress variant="determinate" value={progressPercentage} className={classes.progressBar} color="secondary" />
                </div>
              </Content>
            </PathwayCandidateTile>
          );
        })}
      </div>
    </div>
  );
};

PathwayCandidatesView.propTypes = {
  pathwayInstance: PropTypes.instanceOf(Map).isRequired,
  user: PropTypes.instanceOf(UserModel).isRequired,
  listCandidatePathwaysForPathwayInstance: PropTypes.func.isRequired,
  pathwayCandidates: PropTypes.instanceOf(OrderedSet).isRequired,
  transitionCandidatePathwayStaffState: PropTypes.func.isRequired,
  pathwayDroppedReasonTypes: PropTypes.instanceOf(OrderedSet).isRequired,
  onUpdate: PropTypes.func.isRequired,
};
PathwayCandidatesView.defaultProps = {};

export default PathwayCandidatesView;
